import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import {
  BASE_PATH_TO_LEADERBOARD,
  BASE_PATH_TO_CHALLENGE_PARTICIPANTS,
} from "../../../base_routes_paths/corporate_challenges_paths";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

const PRIMARY = "#4f005f";

// ─── Participant Row ──────────────────────────────────────────────────────────

const ParticipantRow = ({ participant, rank, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3.5}
    onClick={onClick}
    sx={{
      px: 5,
      py: 3,
      cursor: "pointer",
      "&:not(:last-child)": {
        borderBottom: "1px solid",
        borderColor: "divider",
      },
      "&:hover": { backgroundColor: "#fafafa" },
      transition: "background 0.15s",
    }}
  >
    {/* Rank */}
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backgroundColor: "#f0f0f0",
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
        {rank}
      </Typography>
    </Box>

    {/* Avatar */}
    <Avatar
      src={participant.ProfileImage}
      alt={participant.Name}
      sx={{ width: 64, height: 64, flexShrink: 0 }}
    />

    {/* Info */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6" fontWeight={700} color="text.primary" noWrap>
        {participant.Name}
      </Typography>
      {/* <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
        {participant.Email ? (
          <>
            <EmailIcon sx={{ fontSize: 12, color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {participant.Email}
            </Typography>
          </>
        ) : (
          <>
            <PhoneIcon sx={{ fontSize: 12, color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary">
              {participant.Mobile_Number}
            </Typography>
          </>
        )}
      </Stack> */}
    </Box>

    {/* Points */}
    <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 84 }}>
      <Typography variant="h5" fontWeight={700} color={PRIMARY}>
        {participant.TotalPoints}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        pts
      </Typography>
    </Box>
  </Stack>
);

// ─── Skeleton Row ─────────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3.5}
    sx={{ px: 5, py: 3, borderBottom: "1px solid", borderColor: "divider" }}
  >
    <Skeleton variant="circular" width={46} height={46} />
    <Skeleton variant="circular" width={64} height={64} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="35%" height={28} />
    </Box>
    <Skeleton variant="text" width={56} height={28} />
  </Stack>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ChallengeParticipants = () => {
  const { corporateID } = useParams();
  const { token } = AUTH_CONTEXT();

  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add points dialog — step: "input" | "confirm" | null
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(null);

  const handleCloseDialog = () => {
    setSelectedParticipant(null);
    setStep(null);
  };

  const handleAddPointsClick = () => {
    setStep("confirm");
  };

  const handleAddPoints = async () => {
    setStep(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${ARENO_API_ENDPOINT}/challenges/AdminAddPointsOfUserToCompetetiveChallenge`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            CorporateID: corporateID,
            UserID: selectedParticipant._id,
          }),
        }
      );
      const data = await response.json();
      if (
        response.status === 200 &&
        data.status === 200 &&
        data.data?.message === true
      ) {
        toast.success("Points added successfully!", {
          position: "bottom-left",
          autoClose: 3000,
          theme: "light",
        });
        handleCloseDialog();
        fetchParticipants();
      } else {
        toast.error(data.error || "Failed to add points", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchParticipants = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${ARENO_API_ENDPOINT}/challenges/AdminGetCompetetiveChallengeParticipants`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ CorporateID: corporateID }),
        }
      );
      const data = await response.json();
      if (response.status === 200 && data.status === 200) {
        const raw = data.data?.ChallengeData ?? [];
        setParticipants(raw);
      } else {
        toast.error(data.error || "Failed to load participants", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants();
  }, [token, corporateID]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link underline="hover" color="inherit" href={BASE_PATH_TO_HOME}>
          Dashboard
        </Link>
        <Link underline="hover" color="inherit">
          Corporate Challenges
        </Link>
        <Link underline="hover" color="inherit" href={BASE_PATH_TO_CHALLENGE_PARTICIPANTS}>
          Participants
        </Link>
        <Typography color="text.primary" fontWeight={500}>
          {corporateID}
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={4}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              backgroundColor: PRIMARY,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupsIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              lineHeight={1.2}
              color="#181818"
            >
              Participants
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Corporate ID: <strong>{corporateID}</strong>
            </Typography>
          </Box>
        </Stack>

        {!isLoading && (
          <Chip
            icon={<GroupsIcon sx={{ fontSize: 16 }} />}
            label={`${participants.length} participant${
              participants.length !== 1 ? "s" : ""
            }`}
            sx={{
              fontWeight: 700,
              backgroundColor: `${PRIMARY}12`,
              color: PRIMARY,
            }}
          />
        )}
      </Stack>

      {/* Table */}
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {/* Table header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={3.5}
            sx={{
              px: 5,
              py: 2,
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ width: 46, flexShrink: 0 }}>
              <Typography variant="body1" fontWeight={700} color="text.secondary">
                #
              </Typography>
            </Box>
            <Box sx={{ width: 64, flexShrink: 0 }} />
            <Typography
              variant="body1"
              fontWeight={700}
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              PARTICIPANT
            </Typography>
            <Typography
              variant="body1"
              fontWeight={700}
              color="text.secondary"
              sx={{ width: 84, textAlign: "right", flexShrink: 0 }}
            >
              POINTS
            </Typography>
          </Stack>

          {/* Rows */}
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : participants.length === 0 ? (
            <Box
              sx={{
                py: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                color: "text.secondary",
              }}
            >
              <GroupsIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body1" fontWeight={500}>
                No participants found
              </Typography>
            </Box>
          ) : (
            participants.map((p, i) => (
              <ParticipantRow
                key={p._id}
                participant={p}
                rank={i + 1}
                onClick={() => {
                  setSelectedParticipant(p);
                  setStep("input");
                }}
              />
            ))
          )}
        </Paper>
      </Box>

      {/* Add Points Dialog */}
      <Dialog
        open={step === "input"}
        onClose={handleCloseDialog}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 360 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddCircleOutlineIcon sx={{ color: PRIMARY, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={700}>
              Add Points
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {selectedParticipant && (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                src={selectedParticipant.ProfileImage}
                alt={selectedParticipant.Name}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {selectedParticipant.Name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Current points: {selectedParticipant.TotalPoints}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseDialog}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPointsClick}
            sx={{
              flex: 1,
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: "#3a0047" },
            }}
          >
            Add Points
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={step === "confirm"}
        onClose={() => setStep("input")}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Confirm Add Points
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Add points to{" "}
            <strong style={{ color: "#181818" }}>
              {selectedParticipant?.Name}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setStep("input")}
            disabled={isSubmitting}
            sx={{ flex: 1 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPoints}
            disabled={isSubmitting}
            sx={{
              flex: 1,
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: "#3a0047" },
            }}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? "Adding..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChallengeParticipants;
